import { FC, FocusEvent } from 'react';

import { Box, css, styled } from '@mui/material';

import { sharedTestSelectors } from 'tests/shared/shared.selectors';

import {
  LightweightPathwayQuestionServerStructure,
  LightweightPathwayDetailsServerStructure
} from 'fetchers/responses/pathways.response';

import { PathwayQuestionTypes } from 'models/PathwayTemplates';

import { DateQuestion } from 'components/LightweightPathway/DateQuestion';
import {
  DateValue,
  LightweightPathwayDependentQuestionServerStructure,
  LightweightPathwayQuestionAnswer,
  MultipleValue,
  QuestionActionMeta,
  SingleValue,
  TextValue,
  TextArrayValue
} from 'components/LightweightPathway/LightweightPathway.types';
import { getLightweightQuestionAlert } from 'components/LightweightPathway/LightweightPathway.utils';
import { MultipleQuestion } from 'components/LightweightPathway/MultipleQuestion';
import { SingleQuestion } from 'components/LightweightPathway/SingleQuestion';
import { TextArrayQuestion } from 'components/LightweightPathway/TextArrayQuestion';
import { TextQuestion } from 'components/LightweightPathway/TextQuestion';
import { AlertBadge } from 'components/UIkit/atoms/AlertBadge';
import { Text } from 'components/UIkit/atoms/Text';

interface Props {
  question:
    | LightweightPathwayQuestionServerStructure
    | LightweightPathwayDependentQuestionServerStructure;
  pathway: LightweightPathwayDetailsServerStructure;
  index: number;
  answer?: LightweightPathwayQuestionAnswer;
  handleAnswerChanged: (
    newValue: LightweightPathwayQuestionAnswer,
    actionMeta: QuestionActionMeta
  ) => void;
  handleTextQuestionBlur?: (
    event: FocusEvent<HTMLInputElement>,
    currentValue: string,
    valueAfterFocus: string,
    question:
      | LightweightPathwayQuestionServerStructure
      | LightweightPathwayDependentQuestionServerStructure
  ) => void;
  parentQuestionId?: string;
  pathwayIndex: number;
}

export const LightweightPathwayQuestionComponent: FC<Props> = ({
  question,
  pathway,
  index,
  handleTextQuestionBlur,
  answer,
  handleAnswerChanged,
  parentQuestionId,
  pathwayIndex
}) => {
  const alertText = getLightweightQuestionAlert(answer);

  return (
    <StyledContainer
      className="lightweight-pathway-question"
      data-test-hook={sharedTestSelectors.lightweightPathwayQuestionContainer(question.id)}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={8}>
        <Text variant="h4" component="div">
          {question.title}
        </Text>
        {alertText && <AlertBadge alertText={alertText} />}
      </Box>

      {question.type === PathwayQuestionTypes.SINGLE && (
        <SingleQuestion
          question={question}
          pathway={pathway}
          index={index}
          answer={answer as LightweightPathwayQuestionAnswer<SingleValue>}
          handleAnswerChanged={handleAnswerChanged}
          parentQuestionId={parentQuestionId}
          pathwayIndex={pathwayIndex}
        />
      )}

      {question.type === PathwayQuestionTypes.MULTIPLE && (
        <MultipleQuestion
          question={question}
          pathway={pathway}
          index={index}
          answer={answer as LightweightPathwayQuestionAnswer<MultipleValue>}
          handleAnswerChanged={handleAnswerChanged}
          parentQuestionId={parentQuestionId}
          pathwayIndex={pathwayIndex}
        />
      )}

      {question.type === PathwayQuestionTypes.DATE && (
        <DateQuestion
          question={question}
          pathway={pathway}
          index={index}
          answer={answer as LightweightPathwayQuestionAnswer<DateValue>}
          handleAnswerChanged={handleAnswerChanged}
          pathwayIndex={pathwayIndex}
        />
      )}

      {question.type === PathwayQuestionTypes.TEXT && (
        <TextQuestion
          question={question}
          pathway={pathway}
          index={index}
          answer={answer as LightweightPathwayQuestionAnswer<TextValue>}
          handleAnswerChanged={handleAnswerChanged}
          handleTextQuestionBlur={handleTextQuestionBlur}
          pathwayIndex={pathwayIndex}
        />
      )}

      {question.type === PathwayQuestionTypes.TEXT_ARRAY && (
        <TextArrayQuestion
          question={question}
          pathway={pathway}
          index={index}
          answer={answer as LightweightPathwayQuestionAnswer<TextArrayValue>}
          handleAnswerChanged={handleAnswerChanged}
          pathwayIndex={pathwayIndex}
        />
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled(Box)(
  ({ theme }) => css`
    margin-top: ${theme.spacing(20)};
  `
);
